import type { CompiledDerivedTable, DerivedTableResult } from "@packages/commons";

export const GLOBAL_ENTITY = "global";

/** The minimal shape of a graphlib-style graph this module reads from - deliberately loose, since
 * the real type (dagre's `graphlib.Graph`) isn't exported in a form that fits this narrow usage. */
interface GraphLike {
  nodes: () => string[];
  node: (id: string) => GraphNodeLike | undefined;
}

interface GraphNodeLike {
  entity?: string;
  /** The entity-instance index for this node, eg. "1" for the first "children" instance. */
  index?: string | number | null;
  /** Full resolved path, eg. "children/1/some-attribute-guid" - present when the graph is in debug mode. */
  path?: string;
  id?: string;
}

/**
 * Every distinct instance path (eg. "children/1") for `entity` found in the debugger's already-
 * resolved graph. Reads from `record.graph` (parsed) rather than issuing a request of its own -
 * the graph is fetched on every normal debugger run regardless of the derived table view.
 */
export const listEntityInstancePaths = (graph: GraphLike | null | undefined, entity: string): string[] => {
  if (!graph || typeof graph.nodes !== "function") return [];
  const paths = new Set<string>();
  for (const nodeId of graph.nodes()) {
    const node = graph.node(nodeId);
    if (!node || node.entity !== entity || node.index === undefined || node.index === null) continue;
    const path = node.path || node.id;
    if (!path) continue;
    const segments = path.split("/");
    const index = String(node.index);
    // Search from the deepest segment first: for a self-referential entity (one nested under an
    // instance of itself), the node's own instance is the *last* matching entity/index pair in
    // its path, not necessarily the first (an ancestor instance could coincidentally share the
    // same index).
    let at = -1;
    for (let i = segments.length - 2; i >= 0; i--) {
      if (segments[i] === entity && segments[i + 1] === index) {
        at = i;
        break;
      }
    }
    if (at === -1) continue;
    paths.add(segments.slice(0, at + 2).join("/"));
  }
  return Array.from(paths);
};

/**
 * The exact `table` string to request for a table + (optional) selected instance - also used as
 * the request's `id`, so a response can be matched back to the selection that produced it.
 * Returns null when the selection isn't complete enough to fetch yet (eg. an entity-scoped table
 * with no instance chosen) - the caller should not fetch in that case.
 */
export const computeDerivedTableRequestKey = (
  table: Pick<CompiledDerivedTable, "id" | "entity"> | undefined | null,
  selectedInstance: string | null,
): string | null => {
  if (!table) return null;
  if (table.entity === GLOBAL_ENTITY) return table.id;
  return selectedInstance ? `${selectedInstance}/${table.id}` : null;
};

/** The minimal shape of a debugger session record this module reads from. */
interface SessionRecordLike {
  // On the wire this is Rust's `ResultOutput`: absent, a bare object (one `derivedTable` element
  // requested), or an array (more than one requested in the same response). This module only ever
  // requests one at a time, so the array case is treated as "no match" below rather than searched.
  derivedTables?: DerivedTableResult | DerivedTableResult[];
}

/**
 * The fetched result for `requestKey`, or undefined if we don't have one yet (not fetched, still
 * pending, or `record.derivedTables` belongs to a different, now-stale selection).
 */
export const getMatchingDerivedTableResult = (
  record: SessionRecordLike | null | undefined,
  requestKey: string | null,
): DerivedTableResult | undefined => {
  if (!requestKey) return undefined;
  const result = record?.derivedTables;
  if (!result || Array.isArray(result)) return undefined;
  return result.id === requestKey ? result : undefined;
};

/** `null` on the wire means uncertain/unknown/empty - there's no way to distinguish those cases from JSON alone. */
export const formatCellValue = (value: string | number | boolean | string[] | null | undefined): string => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
};
